import { AnyAction, Dispatch } from 'redux';

import { ThunkDispatch } from 'redux-thunk';
import MixpanelService from '../../../utils/mixpanelService';
import {
  CREATE_RECORDING_MESSAGES,
  DELETE_RECORDING_MESSAGE,
  RECORDING_MESSAGE_BULK_OPERATION,
  UPDATE_RECORDING_MESSAGE,
} from '../../../utils/mixpanelEvents';
import { ApplicationState } from '../../rootReducer';
import {
  activeRecordingMessagesPageUpdated,
  getRecordingMessages,
  setIsLoadingRecordingMessage,
  setRecordingMessageSearchTerm,
} from './recordingMessagesReducer';
import { showToastNotification } from '../../ui/toasts/toastsActions';
import { ToastType } from '../../ui/toasts/toastsTypes';
import { getErrorMessage } from '../../../utils/miscUtil';
import _ from 'lodash';
import acApi from '../../../axios/ac/acApi';
import { buildPageableQueryParameters, buildSortingParam } from '../../../utils/pagebleUtil';
import { RecordingMessage } from '../../../types/rest/assetCatalogTypes';
import i18n from '../../../i18n';
import store from '../../configureStore';

/**
 * Fetch messages from backend.
 * @param recordingId - recording id where the messages belong to.
 * @param silentReload - if set to true, loading indicator will not be shown.
 */
export const fetchRecordingMessagesThunk = (recordingId: number, silentReload: boolean) => {
  return async (dispatch: Dispatch, getState: any): Promise<void> => {
    const state: ApplicationState = getState() as ApplicationState;
    if (silentReload !== true) {
      dispatch(setIsLoadingRecordingMessage(true));
    }
    const searchTerm: string = encodeURIComponent(state.recordingMessages.pageFilter.searchTerm);
    // Build the request for recordings, optionally filtered by:
    const pageParams = buildPageableQueryParameters(state.recordingMessages.pageable);
    const sortParam = buildSortingParam(state.recordingMessages.sort);
    const params = {
      ...pageParams,
      filter: searchTerm,
      sort: !_.isNil(sortParam) ? sortParam : '-id',
    };
    try {
      const response = await acApi.recordings.getMessages(recordingId, params);
      // If we have zero results from backend and there are some results available in total, and we are not at first
      // page, go to previous page
      if (
        response.data.result.length === 0 &&
        response.data.total !== 0 &&
        state.recordingMessages.pageable.skip !== 1
      ) {
        const previousPage = state.recordingMessages.pageable.skip - 1 || 1;
        dispatch(updateActiveRecordingMessagesPage(recordingId, previousPage));
      } else {
        const messages = response.data.result;
        const recordingMessagePage = { recordingMessages: messages, total: response.data.total, isLoading: false };
        dispatch(getRecordingMessages(recordingMessagePage));
      }
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of messages', getErrorMessage(e)));
    } finally {
      dispatch(setIsLoadingRecordingMessage(false));
    }
  };
};

export const getRecordingMessagesForIndexModal = async (recordingId: number): Promise<RecordingMessage[]> => {
  let messages = [];
  try {
    const params = {
      limit: -1,
      sortBy: 'index',
    };
    messages = (await acApi.recordings.getMessages(recordingId, params)).data.result;
    return _.orderBy(messages, 'index');
  } catch (e) {}
  return [];
};

const formatMessageDetails = (recordingMessage: RecordingMessage) => {
  return {
    ...recordingMessage,
    headers: recordingMessage.headers.map(item => ({
      name: item.name || '',
      value: item.value || '',
    })),
    properties: recordingMessage.properties.map(item => ({
      name: item.name || '',
      value: item.value || '',
      type: item.type,
    })),
    content: Buffer.from(recordingMessage.content).toString('base64'),
  };
};

export const createRecordingMessage = (recordingMessage: RecordingMessage) => {
  return async (dispatch: Dispatch): Promise<void> => {
    recordingMessage = formatMessageDetails(recordingMessage);
    await acApi.recordings.postMessage(recordingMessage.recordingId, recordingMessage);
    dispatch(
      showToastNotification(
        ToastType.Success,
        `${i18n.t('recordings:actions.messages.successfullyCreatedMessage')} ${recordingMessage.name}`,
      ),
    );
    dispatch(fetchRecordingMessagesThunk(recordingMessage.recordingId, true) as any);
    MixpanelService.track(CREATE_RECORDING_MESSAGES());
  };
};

export const updateRecordingMessage = (recordingMessage: RecordingMessage) => {
  return async (dispatch: Dispatch): Promise<void> => {
    recordingMessage = formatMessageDetails(recordingMessage);
    await acApi.recordings.putMessage(recordingMessage);
    dispatch(
      showToastNotification(
        ToastType.Success,
        `${i18n.t('recordings:actions.messages.successfullyUpdatedMessage')} ${recordingMessage.name}`,
      ),
    );
    dispatch(fetchRecordingMessagesThunk(recordingMessage.recordingId, true) as any);
    MixpanelService.track(UPDATE_RECORDING_MESSAGE());
  };
};

export const updateActiveRecordingMessagesPage = (recordingId: number, activePage: number): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Update the pagination in the store
    await dispatch(activeRecordingMessagesPageUpdated(activePage));
    // Trigger refresh of recording message after update
    await dispatch(fetchRecordingMessagesThunk(recordingId, false));
  };
};

export const updateRecordingMessagesFilterPage = (recordingId: number, searchTerm: string): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    await dispatch(setRecordingMessageSearchTerm(searchTerm));
    dispatch(fetchRecordingMessagesThunk(recordingId, true));
  };
};

export const getRecordingMessageById = async (recordingId: number, messageId: number): Promise<RecordingMessage> => {
  const response = await acApi.recordings.getMessage(recordingId, messageId);
  return response.data.result as RecordingMessage;
};

export const deleteRecordingMessagesBulk = (recordingId: number, messagesIds: number[]) => {
  return async (dispatch: Dispatch): Promise<void> => {
    await acApi.recordings.deleteBulkMessages(recordingId, messagesIds);
    MixpanelService.track(RECORDING_MESSAGE_BULK_OPERATION('delete', messagesIds.length));
    dispatch(showToastNotification(ToastType.Success, i18n.t('recordings:actions.deleteBulkSuccess')));
  };
};

export const deleteRecordingMessagesById = (recordingId: number, message: RecordingMessage) => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>): Promise<void> => {
    const response = await acApi.recordings.deleteMessage(recordingId, message.id);
    if (response.status === 204)
      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('recordings:actions.messages.deleteSingleSuccess', { name: message.name }),
        ),
      );
    MixpanelService.track(DELETE_RECORDING_MESSAGE());
  };
};

export const getSelectedRecordingMessagesLength = (): number => {
  const messages = (store.getState() as ApplicationState).recordingMessages;
  return messages.selectAllType === 'ALL' ? messages.totalMessages : messages.selectedRecordingMessages.length;
};
